import { generateUniqueId } from '@/helpers/uuidUtils';
import { ImpedimentReason, entityTypes, ImpedimentType } from '@/enums/generic';
import { ContactPerson } from '@/models/types/ContactPerson';
import { Address } from '@/models/types/Address';
import { buildingSectionList, entranceList } from '@/enums/building';
import { truncate } from '@/helpers/truncateString';
import { Impediment } from '@/models/Impediment';

/**
 * Any adjustments to the properties of this class
 * should be reflected in the android kotlin class {@see Building.kt}
 */

export class Building {
  public id: string;
  public businessEntityId?: string;
  public address: Address;
  public contactPerson: ContactPerson;
  public buildingSection: string;
  public sectionAddition?: string;
  public entrance: string;
  public entranceAddition?: string;
  public constructionYear?: number;
  public interiorWallType?: string;
  public exteriorWallType?: string;
  public floorType?: string;
  public hasMonumentProtection: boolean;
  public usageType?: string;
  public isAccessible: boolean;
  public reasonNotAccessible?: ImpedimentReason;
  public typeNotAccessible?: ImpedimentType;
  public dateNotAccessible?: string;
  public note?: string;
  public type: string;
  public ordinal: number;
  public impediments?: Impediment[];

  constructor() {
    this.id = generateUniqueId();
    this.businessEntityId = undefined;
    this.address = new Address();
    this.contactPerson = {
      name: '',
      phoneNumber: '',
      role: undefined,
      note: undefined,
    };
    this.buildingSection = '';
    this.sectionAddition = undefined;
    this.entrance = '';
    this.entranceAddition = undefined;
    this.hasMonumentProtection = false;
    this.constructionYear = undefined;
    this.interiorWallType = undefined;
    this.exteriorWallType = undefined;
    this.floorType = undefined;
    this.usageType = undefined;
    this.isAccessible = true;
    this.reasonNotAccessible = undefined;
    this.typeNotAccessible = undefined;
    this.dateNotAccessible = undefined;
    this.note = undefined;
    this.type = entityTypes.building;
    this.ordinal = 0;
  }

  get shortAddress() {
    return this.address.street + ' ' + this.address.houseNumber;
  }

  getParentId() {
    return this.businessEntityId;
  }

  getLabel() {
    if (this.address.street && this.address.houseNumber) {
      return this.address.street + ' ' + this.address.houseNumber;
    }
    return !this.address.street ? '' : this.address.street;
  }

  getEntranceInfo(): string {
    const entranceName = truncate(
      this.entrance === entranceList[3].value
        ? this.entranceAddition
        : entranceList.find((entrance) => entrance.value === this.entrance)
            ?.name,
      20
    );

    const buildingSectionName = truncate(
      this.buildingSection === buildingSectionList[4].value
        ? this.sectionAddition
        : buildingSectionList.find(
            (section) => section.value === this.buildingSection
          )?.name,
      20
    );

    return (
      [entranceName, buildingSectionName].filter(Boolean).join(' / ') || ''
    );
  }

  getIcon() {
    return 'mdi mdi-home';
  }
}
